<template>
  <v-card>
    <v-sheet class="px-3 pt-4 primary lighten-2">
      <v-layout row>
        <v-select v-model="filterValue" :items="filterItems" :item-text=itemText attach dark return-object
          label="Filter" multiple style="width:50%"><template #selection="{ item }">
            <v-chip small light close @input="removeFilter(item.name)" text-color="grey darken-4"
              :color="item.type === 'Severity' ? `${severityToColor(item.severity)} lighten-2`  : 'primary lighten-5'">
              {{item.name}}</v-chip>
          </template>
        </v-select>
        <v-text-field class="ml-4 mb-2" v-model="search" label="Search Alarms" dark flat solo-inverted hide-details
          clearable clear-icon="mdi-close-circle-outline"></v-text-field>
      </v-layout>
    </v-sheet>
    <v-data-table :headers="headers" :items="filteredItems" :search="search" :pagination.sync="pagination"
      :loading="loading">
      <template v-slot:items="props">
        <tr @click="mapFocus(props.item)" class="cursor-pointer">
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.author }}</td>
          <td>{{ props.item.alarmtype }}</td>
          <td :class="`${severityToColor(props.item.severity)} lighten-4`">{{ props.item.severityname }}</td>
          <td>{{ props.item.timestamp ? moment(props.item.timestamp).format("YYYY-MM-DD HH:mm:ss") : '' }}</td>
          <td>{{ props.item.cleared ? moment(props.item.cleared).format("YYYY-MM-DD HH:mm:ss") : '' }}</td>
          <td class="cursor-auto" @click.stop>
            <v-btn small color="secondary" v-if="!props.item.acknowledged" :loading="acknowledgeState[props.item.id] === 1" @click="acknowledge(props)">
              {{acknowledgeState[props.item.id] === 2 ? 'Undo' : 'Acknowledge'}}
            </v-btn>
            <template v-else>
              <template v-if="acknowledgeState[props.item.id] !== 2">{{ moment(props.item.acknowledged).format("YYYY-MM-DD HH:mm:ss")}}</template>
              <v-btn flat :icon="acknowledgeState[props.item.id] !== 2" title="Unacknowledge" @click="acknowledge(props, true)" :loading="acknowledgeState[props.item.id] === 1">
                <v-icon v-if="acknowledgeState[props.item.id] !== 2">undo</v-icon>
                <template v-else>Re-acknowledge</template>
              </v-btn>
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import moment from 'moment'

export default {
  name: 'AlarmTable',
  emits: ['mapFocus'],
  data () {
    return {
      hover: false,
      headers: [
        { text: 'Alarm ID', value: 'id' },
        { text: 'Device ID', value: 'author' },
        { text: 'Alarm Type', value: 'alarmtype' },
        { text: 'Alarm Severity', value: 'severity' },
        { text: 'Raised', value: 'timestamp' },
        { text: 'Cleared', value: 'cleared' },
        { text: 'Acknowledged', value: 'acknowledged' }
      ],
      pagination: {
        sortBy: 'timestamp',
        descending: true
      },
      items: [],
      devices: [],
      search: '',
      loading: true,
      acknowledgeState: {},
      filterValue: [
        { name: 'Active', type: 'Alarm State' },
        { name: 'Unacknowledged', type: 'Acknowledged State' },
        { name: '(2) Warning', type: 'Severity', severity: 2 },
        { name: '(3) Minor', type: 'Severity', severity: 3 },
        { name: '(4) Major', type: 'Severity', severity: 4 },
        { name: '(5) Critical', type: 'Severity', severity: 5 }
      ],
      filterItems: [
        { name: 'Active', type: 'Alarm State' },
        { name: 'Cleared', type: 'Alarm State' },
        { name: 'Unacknowledged', type: 'Acknowledged State' },
        { name: 'Acknowledged', type: 'Acknowledged State' },
        { name: '(0) Normal', type: 'Severity', severity: 0 },
        { name: '(1) Info', type: 'Severity', severity: 1 },
        { name: '(2) Warning', type: 'Severity', severity: 2 },
        { name: '(3) Minor', type: 'Severity', severity: 3 },
        { name: '(4) Major', type: 'Severity', severity: 4 },
        { name: '(5) Critical', type: 'Severity', severity: 5 },
        { name: '(0) Device Functional', type: 'Alarm Type' },
        { name: '(1) Backend Derived', type: 'Alarm Type' },
        { name: '(2) Connectivity', type: 'Alarm Type' },
        { name: '(3) Continuous Flow', type: 'Alarm Type' },
        { name: '(4) No Flow', type: 'Alarm Type' },
        { name: '(5) High Pressure', type: 'Alarm Type' },
        { name: '(6) Low Pressure', type: 'Alarm Type' }
      ],
      alarmTypes: {
        0: '(0) Device Functional', // Device raised alarm
        1: '(1) Backend Derived', // System generic derived alarm
        2: '(2) Connectivity', // System connectivity rules
        3: '(3) Continuous Flow', // System flow rules (JF specific)
        4: '(4) No Flow',
        5: '(5) High Pressure',
        6: '(6) Low Pressure'
      },
      alarmSeverities: {
        0: '(0) Normal',
        1: '(1) Info',
        2: '(2) Warning',
        3: '(3) Minor',
        4: '(4) Major',
        5: '(5) Critical'
      }
    }
  },
  methods: {
    async acknowledge (props, unacknowledge = false) {
      if (this.acknowledgeState[props.item.id] === 2) {
        // Need to unacknowledge
        // Change button to loading
        this.acknowledgeState[props.item.id] = 1

        // Force table to re-render
        this.updateTable()

        try {
          await this.$http.post(`alarmhistory/${unacknowledge ? '' : 'un'}acknowledge/${props.item.id}`)
          // Change button to undo
          this.acknowledgeState[props.item.id] = 0
        } catch (error) {
          this.acknowledgeState[props.item.id] = 2
        }
      } else {
        // Need to acknowledge

        // Change button to loading
        this.acknowledgeState[props.item.id] = 1

        // Force table to re-render
        this.updateTable()

        try {
          await this.$http.post(`alarmhistory/${unacknowledge ? 'un' : ''}acknowledge/${props.item.id}`)
          // Change button to undo
          this.acknowledgeState[props.item.id] = 2
        } catch (error) {
          this.acknowledgeState[props.item.id] = 0
        }
      }
      this.updateTable()
    },
    updateTable () {
      this.pagination.descending = !this.pagination.descending
      this.pagination.descending = !this.pagination.descending
    },
    moment (time) {
      return moment(time)
    },
    /**
     * Open the popup for a given device on the map
     *
     * @param {*} item The alarm history item to find a device for
     */
    mapFocus (item) {
      this.$emit('mapFocus', item.author)
    },
    /**
     * Generate the name to show in the filter select menu
     *
     * @param {*} item Select item
     */
    itemText (item) {
      return `${item.type} - ${item.name}`
    },
    /**
     * Remove a specific filter from the list
     *
     * @param {string} name Name of the filter to remove
     */
    removeFilter (name) {
      this.filterValue = this.filterValue.filter(x => x.name !== name)
    },
    /**
     * Lookup devicename from device ID
     *
     * @param {*} id Device ID to look up
     */
    findDevicenameFromId (id) {
      const device = this.devices.find(x => x.id === id)
      const devicename = device && device.devicename
      return devicename
    },
    /**
     * Convert severity index to Vuetify colour
     *
     * @param {number} severity Severity enum index
     */
    severityToColor (severity) {
      switch (severity) {
        case 1:
          return 'gray'
        case 2:
          return 'blue'
        case 3:
          return 'yellow'
        case 4:
          return 'orange'
        case 5:
          return 'red'
        default:
          return 'green'
      }
    }
  },
  computed: {

    /**
     *  Compute new items list that has filters applied
     *
     *  @returns {Object[]} Filtered item list
     */
    filteredItems () {
      // TODO: Make filter implementation more concise
      const filterNames = this.filterValue.map(x => x.name)
      const filteredList = this.items.filter(x => {
        const none = filterNames.length === 0

        const active = filterNames.includes('Active') && !x.cleared
        const cleared = filterNames.includes('Cleared') && x.cleared
        const alarmStateFiltered = this.filterValue.some(x => x.type === 'Alarm State')
        const alarmStateFilter = alarmStateFiltered ? active || cleared : true

        const unacknowledged = filterNames.includes('Unacknowledged') && !x.acknowledged
        const acknowledged = filterNames.includes('Acknowledged') && x.acknowledged
        const acknowledgedStateFiltered = this.filterValue.some(x => x.type === 'Acknowledged State')
        const acknowledgedStateFilter = acknowledgedStateFiltered ? unacknowledged || acknowledged : true

        const normal = filterNames.includes('(0) Normal') && x.severity === 0
        const info = filterNames.includes('(1) Info') && x.severity === 1
        const warning = filterNames.includes('(2) Warning') && x.severity === 2
        const minor = filterNames.includes('(3) Minor') && x.severity === 3
        const major = filterNames.includes('(4) Major') && x.severity === 4
        const critical = filterNames.includes('(5) Critical') && x.severity === 5
        const severityFiltered = this.filterValue.some(x => x.type === 'Severity')
        const severityFilter = severityFiltered ? normal || info || warning || minor || major || critical : true

        const deviceFunctional = filterNames.includes('(0) Device Functional') && x.alarmtype === '(0) Device Functional'
        const backendDerived = filterNames.includes('(1) Backend Derived') && x.alarmtype === '(1) Backend Derived'
        const connectivity = filterNames.includes('(2) Connectivity') && x.alarmtype === '(2) Connectivity'
        const continuousFlow = filterNames.includes('(3) Continuous Flow') && x.alarmtype === '(3) Continuous Flow'
        const noFlow = filterNames.includes('(4) No Flow') && x.alarmtype === '(4) No Flow'
        const highPressure = filterNames.includes('(5) High Pressure') && x.alarmtype === '(5) High Pressure'
        const lowPressure = filterNames.includes('(6) Low Pressure') && x.alarmtype === '(6) Low Pressure'
        const alarmTypeFiltered = this.filterValue.some(x => x.type === 'Alarm Type')
        const alarmFilter = alarmTypeFiltered ? deviceFunctional || backendDerived || connectivity || continuousFlow || noFlow || highPressure || lowPressure : true

        return none || (alarmStateFilter && severityFilter && acknowledgedStateFilter && alarmFilter)
      })
      return filteredList
    }
  },
  async mounted () {
    const devicesResponse = await this.$http.get('/schemas/devices')
    this.devices = devicesResponse.data.devices
    // Get all time alarm history
    const alarmhistoryResponse = await this.$http.get(`/alarmhistory?datefrom=20200101000000&dateto=${moment().format('YYYYMMDD235959')}`)
    const alarmhistory = alarmhistoryResponse.data.alarmhistory

    this.items = alarmhistory.map(x => {
      const author = this.findDevicenameFromId(x.alarm_author)
      const alarmtype = this.alarmTypes[x.alarmtype]
      const severityname = this.alarmSeverities[x.severity]
      return { ...x, author, alarmtype, severityname }
    })
    this.loading = false
  }
}
</script>

<style>
.cursor-pointer {
  cursor: pointer;
}
.cursor-auto {
  cursor: auto;
}
</style>
